export const ANDROID_APP_PACKAGE = "com.olatundephoster.carpaddynative"
export const APP_UNIVERSAL_LINK_ORIGIN = "https://app.carpaddy.com"
export const VERIFY_PAYMENT_ROUTE = "payments/verify-payment"
export const VERIFY_PAYMENT_PATH = "/payments/verify-payment"
export const PAYMENT_SOURCE_MOBILE = "mobile"
export const PAYMENT_PLATFORM_IOS = "ios"
export const PAYMENT_PLATFORM_ANDROID = "android"
export const NATIVE_APP_SCHEME = "carpaddy://"

export type NativePaymentPlatform =
  | typeof PAYMENT_PLATFORM_IOS
  | typeof PAYMENT_PLATFORM_ANDROID

export const isAndroidUserAgent = (userAgent: string) =>
  /Android/i.test(userAgent)

export const isIosUserAgent = (userAgent: string) =>
  /iPhone|iPad|iPod/i.test(userAgent)

export const buildNativeDeepLinkPath = (
  route: string,
  params?: Record<string, string> | null
) => {
  const search = new URLSearchParams(params || {}).toString()
  return search ? `/${route}?${search}` : `/${route}`
}

export const buildAndroidIntentUrl = (
  path: string,
  query: string,
  browserFallbackUrl: string,
  packageName = ANDROID_APP_PACKAGE
) => {
  const normalizedPath = path.startsWith("/") ? path.slice(1) : path
  const queryString = query.startsWith("?") || !query ? query : `?${query}`

  return (
    `intent://app.carpaddy.com/${normalizedPath}${queryString}` +
    `#Intent;scheme=https;package=${packageName};` +
    `S.browser_fallback_url=${encodeURIComponent(browserFallbackUrl)};end`
  )
}

export const shouldAttemptPaymentAppRedirect = (
  pathname: string,
  search: string,
  userAgent: string,
  isInNativeWebView: boolean
) => {
  if (isInNativeWebView) return false
  if (!pathname.endsWith(VERIFY_PAYMENT_PATH)) return false
  if (!getPaymentReference(search)) return false
  return /Android/i.test(userAgent)
}

export const getPaymentReference = (
  search: string,
  state?: { reference?: string } | null
) => {
  const fromSearch = new URLSearchParams(search).get("reference")
  return fromSearch || state?.reference || undefined
}

export const getVerifyToken = (
  search: string,
  state?: { vt?: string } | null
) => {
  const fromSearch = new URLSearchParams(search).get("vt")
  return fromSearch || state?.vt || undefined
}

export const canVerifyPayment = (
  search: string,
  state?: { reference?: string; vt?: string } | null
) => Boolean(getPaymentReference(search, state) && getVerifyToken(search, state))

export const getPaymentSource = (
  search: string,
  state?: { src?: string } | null
) => {
  const fromSearch = new URLSearchParams(search).get("src")
  return fromSearch || state?.src || undefined
}

export const getPaymentPlatform = (
  search: string,
  state?: { platform?: string } | null
): NativePaymentPlatform | undefined => {
  const fromSearch = new URLSearchParams(search).get("platform")
  const platform = fromSearch || state?.platform

  if (platform === PAYMENT_PLATFORM_IOS || platform === PAYMENT_PLATFORM_ANDROID) {
    return platform
  }

  return undefined
}

const resolvePaymentPlatform = (
  platform: NativePaymentPlatform | undefined,
  userAgent: string
): NativePaymentPlatform | undefined => {
  if (platform) return platform
  if (isAndroidUserAgent(userAgent)) return PAYMENT_PLATFORM_ANDROID
  if (isIosUserAgent(userAgent)) return PAYMENT_PLATFORM_IOS
  return undefined
}

export const buildNativeAppDeepLink = (route: string) => {
  const normalizedRoute = route.startsWith("/") ? route.slice(1) : route
  return `${NATIVE_APP_SCHEME}${normalizedRoute}`
}

export const buildUniversalAppLink = (route: string) => {
  const normalizedPath = route.startsWith("/") ? route : `/${route}`
  return `${APP_UNIVERSAL_LINK_ORIGIN}${normalizedPath}`
}

export const shouldReturnToNativeAppAfterVerify = (
  search: string,
  state: { src?: string } | null | undefined,
  isInNativeWebView: boolean
) => getPaymentSource(search, state) === PAYMENT_SOURCE_MOBILE && !isInNativeWebView

export const buildPostVerifyAppReturnUrl = (
  route: string,
  options: {
    platform?: NativePaymentPlatform
    userAgent?: string
    isInNativeWebView: boolean
  }
) => {
  const { platform, userAgent = "", isInNativeWebView } = options
  const appSchemeUrl = buildNativeAppDeepLink(route)
  const universalAppLink = buildUniversalAppLink(route)
  const resolvedPlatform = resolvePaymentPlatform(platform, userAgent)

  if (isInNativeWebView) {
    return appSchemeUrl
  }

  if (resolvedPlatform === PAYMENT_PLATFORM_ANDROID) {
    return buildAndroidIntentUrl(`/${route}`, "", appSchemeUrl)
  }

  if (resolvedPlatform === PAYMENT_PLATFORM_IOS) {
    return appSchemeUrl
  }

  return universalAppLink
}

export const isPostPaymentDeepLinkRoute = (route: string) => {
  const normalized = route.startsWith("/") ? route.slice(1) : route
  return (
    normalized === "orders" || normalized.startsWith(VERIFY_PAYMENT_ROUTE)
  )
}

export const isPaymentVerified = (data?: {
  status?: boolean | string
  data?: { status?: string }
  error?: unknown
} | null) => {
  if (!data || data.error) return false
  if (data.status === true) return true
  if (data?.data?.status === "success") return true
  return false
}
